import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  ImportIcon,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import { useTimetableVersionDataQuery } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { sortClasses } from '../../../utils/sortHelper';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../graphql/types';
import { createTimetableVersionTableColumns } from '../utils/timetableVersionTableUtils';

type TimetableVersionClassesTableProps = {
  onRowSelection: (row: TimetableVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionUuid: string;
  versionClassesUuids: string[];
  search: string;
  onRemove: (uuids: string[]) => void;
  /** Oeffnet den Klassen-Import; liegt in der Shell, siehe TimetableResourceDataPage. */
  onImportClick: () => void;
};

export const TimetableVersionClassesTable = ({
  onRowSelection,
  onShowLessonClick,
  versionUuid,
  versionClassesUuids,
  search,
  onRemove,
  onImportClick,
}: TimetableVersionClassesTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const store = useTimetableStore();

  const [loadingState] = useReducer(loadingReducer, loadingReducerInitialState);

  const { sorting, saveSorting } = useColumnsSort('timetableVersionClassesTable');
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

  const [{ data: versionData }] = useTimetableVersionDataQuery({
    variables: { versionUuid },
    pause: !versionUuid,
  });
  const classesView = versionData?.timetableVersionData.view.classes;
  const editorLessons = versionData?.timetableVersionData.editor?.lessons;

  const versionClasses: TimetableVersionTableType[] = useMemo(() => {
    return (classesView ?? [])
      .filter((c) => versionClassesUuids.includes(c.uuid))
      .sort(sortClasses)
      .map((c) => {
        const { html, label } = hexToColorOption(c.color ?? '');
        const count = editorLessons?.filter((l) => l.lessonClasses.some((lc) => lc.classUuid === c.uuid)).length ?? 0;

        return {
          uuid: c.uuid,
          name: c.name,
          shortName: c.shortName,
          color: {
            color: html,
            colorLabel: label,
          },
          count,
        };
      });
  }, [classesView, versionClassesUuids, editorLessons]);

  const columns = useMemo(() => createTimetableVersionTableColumns(t('classes.title', { count: 1 })), [t]);

  const canBeRemoved = (uuids: string[]) => {
    return !editorLessons?.some((l) => l.lessonClasses.some((lc) => uuids.includes(lc.classUuid)));
  };

  const { confirm, ConfirmationDialog } = useConfirm();

  const handleRemove = async (uuids: string[]) => {
    const res = await confirm({
      title: t('timetableVersion.removeClasses', { count: uuids.length }),
      message: t('timetableVersion.removeClassesDescription', { count: uuids.length }),
    });

    if (res) {
      onRowSelectionChange({ 0: true });
      onRemove(uuids);
    }
  };

  const readOnly = store.readonly;

  return (
    <>
      <Table<TimetableVersionTableType>
        isDesktop={!isMobile}
        showBorderRadius
        showShadow
        canScroll
        data={versionClasses}
        columns={columns}
        sorting={sorting}
        onSortingChange={saveSorting}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        globalFilter={search}
        showActionBar={false}
        emptyStateSettings={{
          // Nur bei tatsaechlich leerer Liste - bei leerem Filterergebnis rendert die Table denselben
          // Leerzustand; dort bleiben Standardtext und Import-Button aus, weil beides in die Irre fuehrt.
          ...(versionClasses.length === 0
            ? {
                title: t('timetableVersion.emptyState.classes.title'),
                subtitle: t('timetableVersion.emptyState.classes.subtitle'),
              }
            : {}),
          showButton: versionClasses.length === 0 && !readOnly,
          buttonText: t('import.importType', { type: t('classes.title', { count: 2 }) }),
          buttonIcon: <ImportIcon />,
          onButtonClick: onImportClick,
        }}
        minHeight={1050}
        lastColWidth='140px'
        lastCol={(row) => {
          return (
            <ButtonGroup loading={loadingState.some((ls) => ls.uuid === row.original.uuid)}>
              <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                {t('timetableVersion.showLessons')}
              </Button>
              <Dropdown
                noPadding
                trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
              >
                <DropdownMenu
                  data={[
                    {
                      label: t('timetableVersion.editData'),
                      disabled: true,
                      onClick: () => {
                        console.log('edit clicked');
                      },
                    },
                    {
                      label: t('timetableVersion.test'),
                      disabled: true,
                    },
                    {
                      label: t('timetableVersion.remove'),
                      type: 'default',
                      color: 'error',
                      disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                      onClick: async () => {
                        await handleRemove([row.original.uuid]);
                      },
                    },
                  ]}
                />
              </Dropdown>
            </ButtonGroup>
          );
        }}
        showRowHover
        showSelected
        showSelect={false}
        onRowClick={(_event, row) => {
          onRowSelectionChange({ [row.id]: true });
          onRowSelection(row.original, row.index);
          document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <ConfirmationDialog />
    </>
  );
};
